
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarIcon, Save, Clock, BookOpen, GraduationCap, School } from "lucide-react";
import { StudentWithAttendance } from "@/types/attendance";
import { UnifiedLoadingSpinner } from "@/components/loading/UnifiedLoadingSpinner";
import { ArabicCalendar } from "@/components/ui/arabic-calendar";
import { WeekendIndicator } from "../shared/WeekendIndicator";
import { memo } from "react";

interface LateAttendanceControlsProps {
  title: string;
  description?: string;
  selectedDate: string;
  selectedStage: string;
  selectedGrade: string;
  selectedSection: string;
  hasMultipleStages: boolean;
  enabledStages: any[];
  availableGrades: string[];
  availableSections: string[];
  loading?: boolean;
  onDateChange: (date: string) => void;
  onStageChange: (stage: string) => void;
  onGradeChange: (grade: string) => void;
  onSectionChange: (section: string) => void;
  getStageDisplayName: (stageId: string) => string;
}

export const LateAttendanceControls = memo(({
  title,
  description,
  selectedDate,
  selectedStage,
  selectedGrade,
  selectedSection,
  hasMultipleStages,
  enabledStages,
  availableGrades,
  availableSections,
  loading = false,
  onDateChange,
  onStageChange,
  onGradeChange,
  onSectionChange,
  getStageDisplayName,
}: LateAttendanceControlsProps) => {
  const shouldShowStages = hasMultipleStages && enabledStages && enabledStages.length > 1;

  if (loading) {
    return (
      <Card className="bg-white/95 backdrop-blur-sm border-white/30 shadow-lg mb-6">
        <CardContent className="pt-6 flex justify-center">
          <UnifiedLoadingSpinner size="md" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/95 backdrop-blur-sm border-white/30 shadow-lg w-full">
      <CardHeader className="rounded-t-lg p-4 sm:p-6" style={{ backgroundColor: '#11a7a2' }}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2 text-white text-lg">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              {title}
            </CardTitle>
            <CardDescription className="text-sm text-white/90">
            {description}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4 sm:pt-6 p-4 sm:p-6 space-y-6">
        {/* Horizontal Settings Grid */}
        <div className="bg-secondary/5 p-4 rounded-lg border border-secondary/10">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {/* Date Selection */}
          <div>
            <label className="text-sm font-medium text-secondary block mb-2">التاريخ</label>
            <ArabicCalendar
              value={selectedDate ? new Date(selectedDate) : undefined}
              onChange={(date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const localDateString = `${year}-${month}-${day}`;
                onDateChange(localDateString);
              }}
              placeholder="اختر تاريخ التأخر"
              className="border-secondary/30 focus:border-secondary"
            />
          </div>
          {/* Educational Stage Classification - Show if multiple stages */}
          {/* {shouldShowStages && ( */}
            <div>
              <label className="text-sm font-semibold text-secondary flex items-center gap-2 mb-2">
                <School className="h-4 w-4 text-secondary" />
                المرحلة التعليمية
              </label>
              <Select value={selectedStage} onValueChange={onStageChange}>
                <SelectTrigger className="border-secondary/30 focus:border-secondary bg-white h-9">
                  <SelectValue placeholder="اختر المرحلة" />
                </SelectTrigger>
                <SelectContent className="bg-white border-secondary/30 z-50">
                  {enabledStages.map((stage: any) => (
                    <SelectItem key={stage.id} value={stage.id} className="hover:bg-secondary/10">
                      {stage.name || getStageDisplayName(stage.id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          {/* )} */}
          
          {/* Grade Classification */}
          <div>
            <label className="text-sm font-semibold text-secondary flex items-center gap-2 mb-2">
              <GraduationCap className="h-4 w-4 text-secondary" />
              الصف الدراسي
            </label>
            <Select 
              value={selectedGrade} 
              onValueChange={onGradeChange}
              disabled={shouldShowStages && !selectedStage && selectedStage !== "all"}
            >
              <SelectTrigger className="border-secondary/30 focus:border-secondary bg-white h-9">
                <SelectValue placeholder={
                  shouldShowStages && !selectedStage ? "اختر المرحلة أولاً" : "اختر الصف"
                } />
              </SelectTrigger>
              <SelectContent className="bg-white border-secondary/30 z-50">
                {availableGrades.map(grade => (
                  <SelectItem key={grade} value={grade} className="hover:bg-secondary/10">{grade}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Section Classification */}
          <div>
            <label className="text-sm font-semibold text-secondary flex items-center gap-2 mb-2">
              <BookOpen className="h-4 w-4 text-secondary" />
              الشعبة
            </label>
            <Select 
              value={selectedSection} 
              onValueChange={onSectionChange} 
              disabled={!selectedGrade || selectedGrade === "all"} 
            >
              <SelectTrigger className="border-secondary/30 focus:border-secondary bg-white h-9">
                <SelectValue placeholder={!selectedGrade ? "اختر الصف أولاً" : "اختر الشعبة"} />
              </SelectTrigger>
              <SelectContent className="bg-white border-secondary/30 z-50">
                {availableSections.map(section => (
                  <SelectItem key={section} value={section} className="hover:bg-secondary/10">{section}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          </div>
        </div>

        {/* Weekend Indicator */}
        <WeekendIndicator date={selectedDate} />
        
        {/* Message when grade and section are not selected */}
        {!selectedGrade || !selectedSection ? (
          <div className="py-12 text-center bg-secondary/5 rounded-lg border border-secondary/20">
            <div className="w-20 h-20 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-10 h-10 text-secondary" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">اختر الصف والشعبة</h3>
            <p className="text-sm text-gray-600 mb-4">
              يرجى اختيار الصف والشعبة لعرض قائمة الطلاب وتسجيل التأخر/الحضور
            </p>
          </div>
        ) : null}

      </CardContent>
    </Card>
  );
});
